"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import Image from "next/image";
import posthog from "posthog-js";

const navLinks = [
  { name: "Talents", href: "/talents" },
  { name: "Volunteers", href: "/volunteers" },
  { name: "Companies", href: "/companies" },
  { name: "Jobs", href: "/jobs" },
  { name: "About", href: "/about" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  if (pathname.includes('/studio')) {
    return null;
  }

  return (
    <div>

      <nav
        className={cn(
          "mx-auto flex h-14 w-full items-center justify-between overflow-hidden rounded-bl-2xl bg-white/85 px-6 text-xl font-bold shadow backdrop-blur-xs transition-all duration-[25ms] md:h-18 md:max-w-200 md:rounded-full md:p-6 lg:h-20 lg:max-w-300 lg:px-10",
          { "rounded-br-0": isOpen, "rounded-br-2xl delay-[0.2s]": !isOpen },
        )}
      >
        <Link href="/">
          <Image
            src="/rc-logo.webp"
            alt="rc-logo"
            width={350}
            height={39}
            priority
            fetchPriority="high"
            className="h-4.5 w-auto md:h-5.5 lg:h-7.5"
          />
        </Link>
        <ul className="ml-auto hidden gap-4 text-[#011411] md:flex">
          {navLinks.map((link) => (
            <li key={link.name}>
              <Link
                href={link.href}
                onClick={() => posthog.capture('nav_link_clicked', { destination: link.name })}
                className="border-primary group flex flex-col items-center border-b-0 text-[#444] transition-all duration-200"
              >
                <span className={cn(pathname === link.href && "text-primary")}>
                  {link.name}
                </span>
                <span className="bg-primary block h-0.5 w-full max-w-0 transition-all duration-500 group-hover:max-w-full"></span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div
        className="absolute top-0 right-0 z-60 flex h-14 flex-col items-center justify-center gap-2 px-6 text-right md:hidden"
        onClick={() => setIsOpen(!isOpen)}
      >
        <button
          className="ml-auto flex justify-end self-start text-right text-gray-800 md:hidden"
          aria-controls="mobile-menu"
          aria-expanded="false"
          aria-label="Nav Menu Toggle"
        >
          {isOpen ? (
            <X size={28} className="self-end text-right" />
          ) : (
            <Menu size={28} className="self-end text-right" />
          )}
        </button>
      </div>
      <div
        className={cn("absolute inset-0 h-screen w-screen bg-black/80", {
          hidden: !isOpen,
        })}
      ></div>
      <div
        className={cn(
          "absolute inset-0 ml-auto flex h-dvh w-8/10 translate-x-full flex-col justify-end overflow-hidden overscroll-none bg-white pt-8 text-[#444] transition-all duration-200 lg:hidden",
          {
            "translate-x-0 rounded-bl-4xl": isOpen,
          },
        )}
      >
        <div
          className="relative flex h-full w-full items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          <Link href={"/"}>
            <Image
              src={"/rc-logo.webp"}
              alt="menu-logo"
              fill
              className="mx-auto object-contain px-6"
            />
          </Link>
        </div>
        <ul className="divide-primary-dark/15 flex flex-col divide-y">
          {navLinks.map((link, index) => (
            <li
              key={index}
              className="w-full px-6 py-8 text-left text-2xl font-semibold uppercase"
            >
              <Link
                href={link.href}
                onClick={() => {
                  setIsOpen(false);
                  posthog.capture('nav_link_clicked', { destination: link.name });
                }}
                className={cn(
                  "inline-block max-w-max",
                  pathname === link.href && "text-primary",
                )}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
